import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 44, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const ClipReveal = ({ src, alt, className = "", imgClassName = "", delay = 0 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
  >
    <motion.div
      className="h-full w-full overflow-hidden"
      variants={{
        hidden: { clipPath: "inset(100% 0% 0% 0%)" },
        show: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 1.2, delay, ease: EASE } },
      }}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className={`h-full w-full object-cover ${imgClassName}`}
        variants={{
          hidden: { scale: 1.25 },
          show: { scale: 1, transition: { duration: 1.6, delay, ease: EASE } },
        }}
      />
    </motion.div>
  </motion.div>
);

export const Tag = ({ children, dark = false }) => (
  <div className="flex items-center gap-3" data-testid={`tag-${String(children).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <span className="h-px w-8 bg-flame" />
    <span className={`font-mono text-[11px] uppercase tracking-[0.35em] ${dark ? "text-flame" : "text-flame"}`}>
      {children}
    </span>
  </div>
);

export const LineReveal = ({ lines, className = "", lineClassName = "", delay = 0, as: Tag2 = "h2" }) => {
  const M = motion[Tag2] || motion.h2;
  return (
    <M
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
    >
      {lines.map((l, i) => (
        <span key={i} className="mask-line">
          <motion.span
            className={`block ${lineClassName}`}
            variants={{
              hidden: { y: "110%" },
              show: { y: "0%", transition: { duration: 1, delay: delay + i * 0.12, ease: EASE } },
            }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </M>
  );
};

export const Magnetic = ({ children }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: (e.clientX - r.left - r.width / 2) * 0.25, y: (e.clientY - r.top - r.height / 2) * 0.35 });
  };
  return (
    <motion.span
      ref={ref}
      className="inline-block"
      onMouseMove={onMove}
      onMouseLeave={() => setPos({ x: 0, y: 0 })}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: "spring", stiffness: 180, damping: 16, mass: 0.4 }}
    >
      {children}
    </motion.span>
  );
};

export const ArrowLink = ({ to, children, testId, onClick, light = false, big = false }) => {
  const cls = `group inline-flex items-center gap-3 font-mono uppercase tracking-[0.25em] ${
    big ? "text-sm" : "text-[11px]"
  } ${light ? "text-ink" : "text-bone"} transition-colors duration-300 hover:text-flame`;
  const inner = (
    <>
      <span className="relative">
        {children}
        <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 bg-current opacity-30 transition-transform duration-500 group-hover:scale-x-0" />
        <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-flame transition-transform duration-500 group-hover:origin-left group-hover:scale-x-100" />
      </span>
      <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:text-flame" />
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls} onClick={onClick}>
        <Magnetic>{inner}</Magnetic>
      </Link>
    );
  }
  return (
    <button type="button" data-testid={testId} className={cls} onClick={onClick}>
      <Magnetic>{inner}</Magnetic>
    </button>
  );
};

export const SolidButton = ({ children, onClick, testId, type = "button" }) => (
  <Magnetic>
    <button
      type={type}
      data-testid={testId}
      onClick={onClick}
      className="group relative inline-flex items-center gap-3 overflow-hidden bg-flame px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors duration-300"
    >
      <span className="absolute inset-0 origin-bottom scale-y-0 bg-bone transition-transform duration-500 ease-out group-hover:scale-y-100" />
      <span className="relative z-10 transition-colors duration-300 group-hover:text-flame">{children}</span>
      <ArrowRight className="relative z-10 h-4 w-4 transition-all duration-500 group-hover:translate-x-1 group-hover:text-flame" />
    </button>
  </Magnetic>
);

export const Marquee = ({ items, className = "", separator = "·" }) => {
  const row = [...items, ...items];
  return (
    <div className={`overflow-hidden whitespace-nowrap ${className}`} data-testid="editorial-marquee">
      <div className="inline-flex animate-marquee items-center will-change-transform">
        {row.map((it, i) => (
          <span key={i} className="mx-6 inline-flex items-center gap-12 font-serif text-2xl font-light tracking-[0.2em] text-bone/25 sm:text-4xl">
            {it}
            <span className="text-flame/60 text-xl">{separator}</span>
          </span>
        ))}
      </div>
    </div>
  );
};
